import { useCallback, useEffect, useRef, useState } from 'react';
import { api } from '../../lib/api';
import {
  addVaultItem,
  createVault,
  joinVault,
  listVaultItems,
  loadVaultKeys,
  looksLikeRecoveryCode,
  openVaultItems,
  preparePhoto,
  recoverVault,
  reissuePartnerWrap,
  removeVaultItem,
  sealVaultPhoto,
  unlockRootKey,
  vaultErrorMessage,
  PHOTO_MAX_COUNT,
  type VaultItemView,
} from '../../lib/vault';
import type { VaultItem, VaultItemKind, VaultItemMode, VaultKeysView } from '../../../../shared/types';
import { partnerLabel, partnerPossessive } from '../../lib/partnerName';
import './vault.css';

/** One staged photo: prepared once on pick, previewed as the exact string that
 *  will be sealed. */
type PickedPhoto = { name: string; dataUrl: string };

/* ---------------------------------------------------------------------------
   Vault (card t193KwD1i7aY — V3 of the approved Vault build BCDYLuzx6orJ).

   The first user-visible half of the sealed vault: a passphrase gate, letters
   and secrets, and nothing on screen until the vault is unlocked.

   THREE INVARIANTS, each one a line in the card's acceptance list:

   1. LOCKED RENDERS NOTHING. The root key lives in this component's state and
      nowhere else — no sessionStorage, no module singleton, no cache — so a
      reload or leaving the surface discards it and the items list is not even
      fetched until unlock. There is no "remember me" because remembering would
      need the key written somewhere the next script could read.
   2. THE SERVER CANNOT READ. Every write goes through shared/vault-crypto: the
      title and body are sealed under the root key with the item id as AAD, and
      only `pv:v1:` ciphertext is posted.
   3. A FAILED UNLOCK EXPLAINS ITSELF. `vault_bad_passphrase` becomes one plain
      sentence; no key material, no server text, no count of attempts.

   Placement (decided at build time, per the card): a flat destination row in
   the More drawer opening the same panel-over-the-tabs seam Help and Settings
   use — this is a private room, not a sixth tab.
   --------------------------------------------------------------------------- */

type Phase = 'loading' | 'create' | 'unlock' | 'open';
type JoinForm = 'closed' | 'open';

interface VaultProps {
  onClose: () => void;
}

const MIN_PASSPHRASE = 8;

export function Vault({ onClose }: VaultProps) {
  const [phase, setPhase] = useState<Phase>('loading');
  const [keys, setKeys] = useState<VaultKeysView | null>(null);
  /** The couple root key, base64. Component state ONLY — this is the lock. */
  const [rootKey, setRootKey] = useState<string | null>(null);
  const [items, setItems] = useState<VaultItemView[]>([]);
  /** Raw server rows beside the opened views — they carry `sealed` and the
   *  lifecycle fields (mode / ttlHours) the row control reads and writes. */
  const [rawItems, setRawItems] = useState<VaultItem[]>([]);
  /** Rows that were listed before and are absent now: consumed or expired.
   *  The server excludes them from the list, so they stay visible as "Gone"
   *  instead of vanishing silently (lane Rj6lSh7lYDKO). */
  const [goneItems, setGoneItems] = useState<VaultItemView[]>([]);
  const listedRef = useRef<Set<string>>(new Set());
  const lastViewRef = useRef<Map<string, VaultItemView>>(new Map());
  const deletedRef = useRef<Set<string>>(new Set());
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const [passphrase, setPassphrase] = useState('');
  const [passphrase2, setPassphrase2] = useState('');
  const [joinForm, setJoinForm] = useState<JoinForm>('closed');
  const [existingPass, setExistingPass] = useState('');
  const [ownPass, setOwnPass] = useState('');
  const [ownPass2, setOwnPass2] = useState('');

  /* V5 recovery. `freshRecovery` is the code as minted — held in memory only,
     shown once and CLEARED when dismissed, because the point of a recovery code
     is that only a person has it. */
  const [freshRecovery, setFreshRecovery] = useState('');
  const [recoveryOpen, setRecoveryOpen] = useState(false);
  const [recoveryCode, setRecoveryCode] = useState('');
  const [recoveryPass, setRecoveryPass] = useState('');
  const [recoveryPass2, setRecoveryPass2] = useState('');
  const [partnerOpen, setPartnerOpen] = useState(false);
  const [partnerPass, setPartnerPass] = useState('');
  const [partnerPass2, setPartnerPass2] = useState('');
  const [partnerNote, setPartnerNote] = useState('');
  const [kind, setKind] = useState<VaultItemKind>('letter');
  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');
  /** Photos picked from the device, prepared once and previewed as the exact
   *  strings that will be sealed — no second encode, so what you see is what is
   *  stored. A batch is several of them (card cUM7Nn4IfCyz). */
  const [photos, setPhotos] = useState<PickedPhoto[]>([]);
  const [revealed, setRevealed] = useState<Record<string, boolean>>({});

  const mounted = useRef(true);
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  /* ---- read the wraps (never a key) and pick the opening state ---------- */
  useEffect(() => {
    let live = true;
    (async () => {
      try {
        const view = await loadVaultKeys();
        if (!live) return;
        setKeys(view);
        setPhase(view && view.wraps.length > 0 ? 'unlock' : 'create');
      } catch (e) {
        if (!live) return;
        setError(vaultErrorMessage(e));
        setPhase('create');
      }
    })();
    return () => {
      live = false;
    };
  }, []);

  const loadItems = useCallback(async (key: string) => {
    const rows = await listVaultItems();
    const opened = await openVaultItems(key, rows);
    setRawItems(rows);
    const live = new Set(rows.map((r) => r.id));
    setGoneItems((cur) => {
      const next = cur.filter((g) => !live.has(g.id) && !deletedRef.current.has(g.id));
      for (const id of listedRef.current) {
        if (!live.has(id) && !deletedRef.current.has(id)) {
          const last = lastViewRef.current.get(id);
          if (last && !next.some((g) => g.id === id)) next.push(last);
        }
      }
      return next;
    });
    listedRef.current = live;
    lastViewRef.current = new Map(opened.map((v) => [v.id, v]));
    return opened;
  }, []);

  /** Lock = drop the key and every opened item from memory. */
  const lock = useCallback(() => {
    setRootKey(null);
    setItems([]);
    setRawItems([]);
    setGoneItems([]);
    setRevealed({});
    listedRef.current = new Set();
    lastViewRef.current = new Map();
    deletedRef.current = new Set();
    setBusy(false);
    setError('');
    setPassphrase('');
    setPassphrase2('');
    setJoinForm('closed');
    setExistingPass('');
    setOwnPass('');
    setOwnPass2('');
    setRecoveryCode('');
    setRecoveryPass('');
    setRecoveryPass2('');
    setRecoveryOpen(false);
    setPartnerPass('');
    setPartnerPass2('');
    setPartnerOpen(false);
    setPartnerNote('');
    setTitle('');
    setBody('');
    setPhotos([]);
    setPhase('unlock');
  }, []);

  const close = useCallback(() => {
    lock();
    onClose();
  }, [lock, onClose]);

  /* Escape closes, like every other panel-over-tabs screen. */
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') close();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [close]);

  const unlock = async (e: React.FormEvent) => {
    e.preventDefault();
    if (busy) return;
    if (!passphrase) {
      setError('Enter your passphrase to unlock.');
      return;
    }
    setBusy(true);
    setError('');
    try {
      const current = keys ?? (await loadVaultKeys());
      if (current && !keys) setKeys(current);
      const key = current ? await unlockRootKey(current, passphrase) : null;
      if (!key) throw new Error('vault_bad_passphrase');
      const opened = await loadItems(key);
      if (!mounted.current) return;
      setRootKey(key);
      setItems(opened);
      setPassphrase('');
      setPhase('open');
    } catch (err) {
      if (mounted.current) setError(vaultErrorMessage(err));
    } finally {
      if (mounted.current) setBusy(false);
    }
  };

  const enrol = async (e: React.FormEvent) => {
    e.preventDefault();
    if (busy) return;
    if (passphrase.length < MIN_PASSPHRASE) {
      setError(`Use at least ${MIN_PASSPHRASE} characters — this passphrase is the only way in.`);
      return;
    }
    if (passphrase !== passphrase2) {
      setError('Those two passphrases do not match.');
      return;
    }
    setBusy(true);
    setError('');
    try {
      const { rootKey: key, recoveryCode: code } = await createVault(passphrase);
      // The mount-time read ran before this wrap existed, so the stored wraps
      // must be re-read here — otherwise the first Lock in this session has no
      // keys to try and reports a false "does not match this vault".
      setKeys(await loadVaultKeys());
      const opened = await loadItems(key);
      if (!mounted.current) return;
      setRootKey(key);
      setItems(opened);
      setPassphrase('');
      setPassphrase2('');
      setFreshRecovery(code);
      setPhase('open');
    } catch (err) {
      if (mounted.current) setError(vaultErrorMessage(err));
    } finally {
      if (mounted.current) setBusy(false);
    }
  };

  /**
   * Recovery (V5): the code alone opens the root key, then the member picks a
   * new passphrase and their wrap is replaced — the old passphrase stops
   * working in the same step. There is no server-side half: if the code is lost
   * and both passphrases are forgotten, the vault content is unrecoverable, and
   * the copy below says so rather than implying support can help.
   */
  const recover = async (e: React.FormEvent) => {
    e.preventDefault();
    if (busy) return;
    if (!looksLikeRecoveryCode(recoveryCode)) {
      setError('A recovery code is 20 letters and numbers, in five groups of four.');
      return;
    }
    if (recoveryPass.length < MIN_PASSPHRASE) {
      setError(`Use at least ${MIN_PASSPHRASE} characters for your new passphrase.`);
      return;
    }
    if (recoveryPass !== recoveryPass2) {
      setError('Those two passphrases do not match.');
      return;
    }
    setBusy(true);
    setError('');
    try {
      const current = keys ?? (await loadVaultKeys());
      if (!current) throw new Error('vault_no_recovery');
      const key = await recoverVault(current, recoveryCode, recoveryPass);
      setKeys(await loadVaultKeys());
      const opened = await loadItems(key);
      if (!mounted.current) return;
      setRootKey(key);
      setItems(opened);
      setRecoveryCode('');
      setRecoveryPass('');
      setRecoveryPass2('');
      setRecoveryOpen(false);
      setPhase('open');
    } catch (err) {
      if (mounted.current) setError(vaultErrorMessage(err));
    } finally {
      if (mounted.current) setBusy(false);
    }
  };

  /** Partner re-issue (V5): the other member's slot, re-wrapped for a new passphrase. */
  const reissue = async (e: React.FormEvent) => {
    e.preventDefault();
    if (busy || !rootKey || !keys?.ownSlot) return;
    if (partnerPass.length < MIN_PASSPHRASE) {
      setError(`Use at least ${MIN_PASSPHRASE} characters for their new passphrase.`);
      return;
    }
    if (partnerPass !== partnerPass2) {
      setError('Those two passphrases do not match.');
      return;
    }
    setBusy(true);
    setError('');
    setPartnerNote('');
    try {
      const target = keys.ownSlot === 'a' ? 'b' : 'a';
      const next = await reissuePartnerWrap(rootKey, target, partnerPass);
      if (!mounted.current) return;
      setKeys(next);
      setPartnerPass('');
      setPartnerPass2('');
      setPartnerOpen(false);
      setPartnerNote('Their new passphrase is set. The old one no longer opens the vault.');
    } catch (err) {
      if (mounted.current) setError(vaultErrorMessage(err));
    } finally {
      if (mounted.current) setBusy(false);
    }
  };

  const join = async (e: React.FormEvent) => {
    e.preventDefault();
    if (busy || !keys) return;
    if (ownPass.length < MIN_PASSPHRASE) {
      setError(`Use at least ${MIN_PASSPHRASE} characters for your own passphrase.`);
      return;
    }
    if (ownPass !== ownPass2) {
      setError('Those two passphrases do not match.');
      return;
    }
    setBusy(true);
    setError('');
    try {
      const key = await joinVault(keys, existingPass, ownPass);
      // Same reason as enrol: this member's slot is new, so the wraps the
      // screen was mounted with do not include it yet.
      setKeys(await loadVaultKeys());
      const opened = await loadItems(key);
      if (!mounted.current) return;
      setRootKey(key);
      setItems(opened);
      setExistingPass('');
      setOwnPass('');
      setOwnPass2('');
      setJoinForm('closed');
      setPhase('open');
    } catch (err) {
      if (mounted.current) setError(vaultErrorMessage(err));
    } finally {
      if (mounted.current) setBusy(false);
    }
  };

  const save = async (e: React.FormEvent) => {
    e.preventDefault();
    if (busy || !rootKey) return;
    if (kind === 'photo' && photos.length === 0) {
      setError('Pick a photo first.');
      return;
    }
    if (kind !== 'photo' && !title.trim() && !body.trim()) {
      setError('Add a title or some words first.');
      return;
    }
    setBusy(true);
    setError('');
    try {
      if (kind === 'photo' && photos.length > 0) {
        const base = title.trim() || 'Photo';
        /* One pass over the batch: a single photo keeps the plain title, a
           batch names each by its position so the album reads in pick order. */
        for (let i = 0; i < photos.length; i += 1) {
          const name = photos.length > 1 ? `${base} ${i + 1}` : base;
          await sealVaultPhoto(rootKey, name, photos[i].dataUrl);
        }
        setPhotos([]);
      } else {
        await addVaultItem(rootKey, kind, title.trim(), body);
      }
      const opened = await loadItems(rootKey);
      if (!mounted.current) return;
      setItems(opened);
      setTitle('');
      setBody('');
    } catch (err) {
      if (mounted.current) setError(vaultErrorMessage(err));
    } finally {
      if (mounted.current) setBusy(false);
    }
  };

  /** Downscale on pick, not on save: the preview is the sealed string.
   *
   *  A phone hands back several files at once (`multiple` on the library
   *  picker) or one from the camera, so the batch is prepared in pick order and
   *  appended to whatever was already staged. The couple's photo ceiling is
   *  checked BEFORE any encoding: an over-cap batch is refused with a sentence
   *  rather than sealed halfway. */
  const pickPhotos = async (files: FileList | null) => {
    if (!files || files.length === 0 || busy) return;
    const picked = Array.from(files);
    const kept = items.filter(i => i.kind === 'photo').length + photos.length;
    if (kept + picked.length > PHOTO_MAX_COUNT) {
      setError(`This vault keeps ${PHOTO_MAX_COUNT} photos. Remove one before adding more.`);
      return;
    }
    setBusy(true);
    setError('');
    try {
      const next: PickedPhoto[] = [];
      for (const file of picked) {
        next.push({ name: file.name, dataUrl: await preparePhoto(file) });
      }
      if (mounted.current) setPhotos(cur => [...cur, ...next]);
    } catch (err) {
      if (mounted.current) setError(vaultErrorMessage(err));
    } finally {
      if (mounted.current) setBusy(false);
    }
  };

  const remove = async (id: string) => {
    if (busy || !rootKey) return;
    setBusy(true);
    setError('');
    try {
      await removeVaultItem(id);
      // A hard delete is not a lifecycle "gone" — keep it out of that state.
      deletedRef.current.add(id);
      const opened = await loadItems(rootKey);
      if (mounted.current) setItems(opened);
    } catch (err) {
      if (mounted.current) setError(vaultErrorMessage(err));
    } finally {
      if (mounted.current) setBusy(false);
    }
  };

  const modeOf = (id: string): VaultItemMode => rawItems.find((r) => r.id === id)?.mode ?? 'keep';
  const ttlOf = (id: string): number => rawItems.find((r) => r.id === id)?.ttlHours ?? 24;

  /** Lifecycle control (lane Rj6lSh7lYDKO): re-POSTs the row with the chosen
   *  mode; 'delete' is the existing hard delete. The server upserts only the
   *  lifecycle fields on a known id, so the sealed row is never rewritten. */
  const setItemMode = async (id: string, mode: VaultItemMode | 'delete', ttlHours: number | null) => {
    if (busy || !rootKey) return;
    if (mode === 'delete') {
      await remove(id);
      return;
    }
    const raw = rawItems.find((r) => r.id === id);
    if (!raw) return;
    setBusy(true);
    setError('');
    try {
      await api('/api/vault', {
        body: {
          id: raw.id,
          kind: raw.kind,
          sealed: raw.sealed,
          dekWrap: raw.dekWrap,
          dekIv: raw.dekIv,
          vrkVersion: raw.vrkVersion,
          mode,
          ttlHours,
        },
      });
      const opened = await loadItems(rootKey);
      if (mounted.current) setItems(opened);
    } catch (err) {
      if (mounted.current) setError(vaultErrorMessage(err));
    } finally {
      if (mounted.current) setBusy(false);
    }
  };

  return (
    <section className="vault" aria-labelledby="vault-title" data-testid="vault-screen" data-vault-phase={phase}>
      <div className="vault-head">
        <h2 className="vault-title" id="vault-title">
          Vault
        </h2>
        <button type="button" className="vault-close" id="btn-vault-close" onClick={close}>
          Close
        </button>
      </div>

      {phase === 'loading' && (
        <p className="vault-lede" id="vault-loading">
          Opening the vault…
        </p>
      )}

      {/* ---- first run: there is no vault yet ---------------------------- */}
      {phase === 'create' && (
        <form className="vault-card" id="vault-create-form" onSubmit={enrol}>
          <p className="vault-lede">
            The vault is the one room in Pear only the two of you can open. Letters for later,
            passwords you share — sealed on this device before they are saved, so nobody on our
            side can read them. Not us, not a support tool.
          </p>
          <p className="vault-note">
            Choose a vault passphrase. It is separate from your login. We will show you a
            recovery code straight after — save it somewhere safe, because losing your passphrase
            and that code means the vault cannot be opened again.
          </p>
          <label className="vault-label" htmlFor="vault-passphrase">
            Vault passphrase
          </label>
          <input
            id="vault-passphrase"
            className="vault-input"
            type="password"
            autoComplete="new-password"
            value={passphrase}
            onChange={e => setPassphrase(e.target.value)}
          />
          <label className="vault-label" htmlFor="vault-passphrase2">
            Again
          </label>
          <input
            id="vault-passphrase2"
            className="vault-input"
            type="password"
            autoComplete="new-password"
            value={passphrase2}
            onChange={e => setPassphrase2(e.target.value)}
          />
          <button type="submit" className="vault-primary" id="btn-vault-create" disabled={busy}>
            {busy ? 'Creating…' : 'Create the vault'}
          </button>
        </form>
      )}

      {/* ---- locked ------------------------------------------------------ */}
      {phase === 'unlock' && (
        <div className="vault-card vault-locked" id="vault-locked">
          <p className="vault-lede" id="vault-locked-lede">
            Locked. Your vault passphrase unlocks it on this device — nothing here is readable
            until then, and it locks again when you leave.
          </p>
          <form id="vault-unlock-form" onSubmit={unlock}>
            <label className="vault-label" htmlFor="vault-pass">
              Vault passphrase
            </label>
            <input
              id="vault-pass"
              className="vault-input"
              type="password"
              autoComplete="current-password"
              value={passphrase}
              onChange={e => setPassphrase(e.target.value)}
            />
            <button type="submit" className="vault-primary" id="btn-vault-unlock" disabled={busy}>
              {busy ? 'Unlocking…' : 'Unlock'}
            </button>
          </form>

          {/* Lost passphrase -> recovery code (V5). Deliberately below the
              unlock form: it is the fallback, not the way in. */}
          {!recoveryOpen && (
            <button
              type="button"
              className="vault-link"
              id="btn-vault-recovery-open"
              onClick={() => {
                setRecoveryOpen(true);
                setError('');
              }}
            >
              Lost your passphrase? Use your recovery code
            </button>
          )}

          {recoveryOpen && (
            <form id="vault-recovery-form" onSubmit={recover}>
              <p className="vault-note" id="vault-recovery-note">
                Your recovery code opens the vault once, so you can set a new passphrase. It was
                shown when the vault was created — we do not keep a copy of it, and neither does
                anyone else.
              </p>
              <label className="vault-label" htmlFor="vault-recovery-code">
                Recovery code
              </label>
              <input
                id="vault-recovery-code"
                className="vault-input vault-code"
                type="text"
                autoComplete="off"
                spellCheck={false}
                value={recoveryCode}
                onChange={e => setRecoveryCode(e.target.value)}
              />
              <label className="vault-label" htmlFor="vault-recovery-pass">
                New vault passphrase
              </label>
              <input
                id="vault-recovery-pass"
                className="vault-input"
                type="password"
                autoComplete="new-password"
                value={recoveryPass}
                onChange={e => setRecoveryPass(e.target.value)}
              />
              <label className="vault-label" htmlFor="vault-recovery-pass2">
                Again
              </label>
              <input
                id="vault-recovery-pass2"
                className="vault-input"
                type="password"
                autoComplete="new-password"
                value={recoveryPass2}
                onChange={e => setRecoveryPass2(e.target.value)}
              />
              <button type="submit" className="vault-primary" id="btn-vault-recover" disabled={busy}>
                {busy ? 'Recovering…' : 'Recover the vault'}
              </button>
            </form>
          )}

          {/* A second member has no wrap yet: they join with the passphrase
              that already opens the vault, then pick their own. */}
          {keys && keys.wraps.length < 2 && joinForm === 'closed' && (
            <button
              type="button"
              className="vault-link"
              id="btn-vault-join"
              onClick={() => {
                setJoinForm('open');
                setError('');
              }}
            >
              First time for you? Use the vault passphrase {partnerLabel()} set
            </button>
          )}

          {joinForm === 'open' && (
            <form id="vault-join-form" onSubmit={join}>
              <label className="vault-label" htmlFor="vault-join-existing">
                The passphrase already on this vault
              </label>
              <input
                id="vault-join-existing"
                className="vault-input"
                type="password"
                autoComplete="off"
                value={existingPass}
                onChange={e => setExistingPass(e.target.value)}
              />
              <label className="vault-label" htmlFor="vault-join-own">
                Your own passphrase
              </label>
              <input
                id="vault-join-own"
                className="vault-input"
                type="password"
                autoComplete="new-password"
                value={ownPass}
                onChange={e => setOwnPass(e.target.value)}
              />
              <label className="vault-label" htmlFor="vault-join-own2">
                Again
              </label>
              <input
                id="vault-join-own2"
                className="vault-input"
                type="password"
                autoComplete="new-password"
                value={ownPass2}
                onChange={e => setOwnPass2(e.target.value)}
              />
              <button type="submit" className="vault-primary" id="btn-vault-join-save" disabled={busy}>
                {busy ? 'Saving…' : 'Use my own passphrase'}
              </button>
            </form>
          )}
        </div>
      )}

      {/* ---- unlocked ---------------------------------------------------- */}
      {phase === 'open' && (
        <div id="vault-open">
          <div className="vault-open-bar">
            <span className="vault-badge" id="vault-unlocked-badge">
              Unlocked
            </span>
            <button type="button" className="vault-link" id="btn-vault-lock" onClick={lock}>
              Lock
            </button>
          </div>

          {freshRecovery && (
            <div className="vault-card vault-recovery-once" id="vault-recovery-once">
              <p className="vault-note">
                This is your recovery code. Write it down and keep it somewhere only you can get
                to — it is the way back in if you forget your passphrase. It will not be shown
                again.
              </p>
              <p className="vault-code" id="vault-recovery-once-code" data-testid="vault-recovery-once-code">
                {freshRecovery}
              </p>
              <button
                type="button"
                className="vault-link"
                id="btn-vault-recovery-saved"
                onClick={() => setFreshRecovery('')}
              >
                I have saved it
              </button>
            </div>
          )}

          <form className="vault-card" id="vault-compose" onSubmit={save}>
            <div className="vault-kinds" role="group" aria-label="What are you keeping?">
              <button
                type="button"
                className={`vault-kind${kind === 'letter' ? ' is-on' : ''}`}
                id="vault-kind-letter"
                aria-pressed={kind === 'letter'}
                onClick={() => setKind('letter')}
              >
                Letter
              </button>
              <button
                type="button"
                className={`vault-kind${kind === 'secret' ? ' is-on' : ''}`}
                id="vault-kind-secret"
                aria-pressed={kind === 'secret'}
                onClick={() => setKind('secret')}
              >
                Secret
              </button>
              <button
                type="button"
                className={`vault-kind${kind === 'photo' ? ' is-on' : ''}`}
                id="vault-kind-photo"
                aria-pressed={kind === 'photo'}
                onClick={() => setKind('photo')}
              >
                Photo
              </button>
            </div>
            <label className="vault-label" htmlFor="vault-item-title">
              {kind === 'photo' ? 'A name for it (optional)' : 'Title'}
            </label>
            <input
              id="vault-item-title"
              className="vault-input"
              type="text"
              maxLength={120}
              placeholder={
                kind === 'letter' ? 'For our tenth year' : kind === 'secret' ? 'The safe code' : 'The day we moved in'
              }
              value={title}
              onChange={e => setTitle(e.target.value)}
            />
            {kind === 'photo' ? (
              <>
                <div className="vault-photo-pick">
                  <label className="vault-photo-pick-label" htmlFor="vault-photo-input">
                    Choose photos
                  </label>
                  <input
                    id="vault-photo-input"
                    className="vault-file vault-file-native"
                    type="file"
                    accept="image/*"
                    multiple
                    data-testid="vault-photo-input"
                    onChange={e => {
                      void pickPhotos(e.currentTarget.files ?? null);
                      e.currentTarget.value = '';
                    }}
                  />
                </div>
                {/* Founder email 2026-10-28: on a laptop the native control
                    said "Choose File" and the camera row opened a file picker
                    anyway (capture is ignored on desktop), so desktop gets the
                    library door only and the phone keeps the camera. Both
                    labels are ours now, so neither reads "Choose File". */}
                <div className="vault-photo-pick vault-photo-camera">
                  <label className="vault-photo-pick-label" htmlFor="vault-photo-camera">
                    Open camera
                  </label>
                  <input
                    id="vault-photo-camera"
                    className="vault-file vault-file-native"
                    type="file"
                    accept="image/*"
                    capture="environment"
                    data-testid="vault-photo-camera"
                    onChange={e => {
                      void pickPhotos(e.currentTarget.files ?? null);
                      e.currentTarget.value = '';
                    }}
                  />
                </div>
                {photos.length > 0 && (
                  <div className="vault-photo-picked" id="vault-photo-picked">
                    {photos.map((picked, index) => (
                      <div
                        className="vault-photo-picked-row"
                        key={`${picked.name}-${index}`}
                        data-vault-photo-picked={index}
                      >
                        <img
                          className="vault-photo-preview"
                          id={index === 0 ? 'vault-photo-preview' : undefined}
                          src={picked.dataUrl}
                          alt={
                            photos.length > 1
                              ? `Photo ${index + 1} you picked, not yet sealed`
                              : 'The photo you picked, not yet sealed'
                          }
                        />
                        <span className="vault-photo-meta">
                          {picked.name} — sealed on this device when you save.
                        </span>
                        <button
                          type="button"
                          className="vault-photo-remove"
                          data-vault-photo-remove={index}
                          onClick={() => setPhotos(cur => cur.filter((_, n) => n !== index))}
                        >
                          Remove
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </>
            ) : (
              <>
                <label className="vault-label" htmlFor="vault-item-body">
                  {kind === 'letter' ? 'The letter' : 'The secret'}
                </label>
                <textarea
                  id="vault-item-body"
                  className="vault-input vault-textarea"
                  rows={kind === 'letter' ? 6 : 3}
                  value={body}
                  onChange={e => setBody(e.target.value)}
                />
              </>
            )}
            <button type="submit" className="vault-primary" id="btn-vault-save" disabled={busy}>
              {busy ? 'Sealing…' : 'Seal it'}
            </button>
          </form>

          <h3 className="vault-section-title" id="vault-items-title">
            Sealed in here
          </h3>
          {items.length === 0 && goneItems.length === 0 ? (
            <p className="vault-empty" id="vault-empty">
              Nothing sealed yet. A letter for an anniversary is a good first one.
            </p>
          ) : (
            <ul className="vault-items" aria-labelledby="vault-items-title">
              {items.map(item => (
                <li className="vault-item" key={item.id} data-vault-item={item.id} data-vault-kind={item.kind}>
                  <div className="vault-item-head">
                    <span className={`vault-item-kind vault-item-kind--${item.kind}`}>
                      {item.kind === 'letter' ? 'Letter' : item.kind === 'secret' ? 'Secret' : 'Photo'}
                    </span>
                    <span className="vault-item-when">
                      {new Date(item.createdAt).toLocaleDateString(undefined, {
                        day: 'numeric',
                        month: 'short',
                        year: 'numeric',
                      })}
                    </span>
                  </div>
                  {item.title && <p className="vault-item-title">{item.title}</p>}
                  {item.kind === 'photo' && item.body.startsWith('data:image/') ? (
                    <img
                      className="vault-photo"
                      data-vault-photo={item.id}
                      src={item.body}
                      alt={item.title || 'A sealed photo'}
                    />
                  ) : (
                    <p className={`vault-item-body${item.kind === 'secret' && !revealed[item.id] ? ' is-masked' : ''}`}>
                      {item.kind === 'secret' && !revealed[item.id] ? '••••••••••' : item.body}
                    </p>
                  )}
                  <div className="vault-item-actions">
                    {item.kind === 'secret' && (
                      <button
                        type="button"
                        className="vault-link"
                        data-vault-reveal={item.id}
                        onClick={() => setRevealed(r => ({ ...r, [item.id]: !r[item.id] }))}
                      >
                        {revealed[item.id] ? 'Hide' : 'Show'}
                      </button>
                    )}
                    <button
                      type="button"
                      className="vault-link vault-danger"
                      data-vault-delete={item.id}
                      disabled={busy}
                      onClick={() => remove(item.id)}
                    >
                      Delete
                    </button>
                    {/* Lifecycle (lane Rj6lSh7lYDKO): one control offering the
                        four modes; the TTL select appears only for expireAfter. */}
                    <select
                      className="vault-select"
                      data-vault-mode={item.id}
                      aria-label="Item lifecycle"
                      disabled={busy}
                      value={modeOf(item.id)}
                      onChange={e => {
                        const next = e.target.value;
                        if (next === 'delete') void setItemMode(item.id, 'delete', null);
                        else if (next === 'expireAfter') void setItemMode(item.id, 'expireAfter', ttlOf(item.id));
                        else void setItemMode(item.id, next as VaultItemMode, null);
                      }}
                    >
                      <option value="keep">Keep</option>
                      <option value="expireOnView">Expire on view</option>
                      <option value="expireAfter">Expire after…</option>
                      <option value="delete">Delete</option>
                    </select>
                    {modeOf(item.id) === 'expireAfter' && (
                      <select
                        className="vault-select"
                        data-vault-ttl={item.id}
                        aria-label="Expire after how long"
                        disabled={busy}
                        value={ttlOf(item.id)}
                        onChange={e => void setItemMode(item.id, 'expireAfter', Number(e.target.value))}
                      >
                        <option value={1}>1 hour</option>
                        <option value={24}>1 day</option>
                        <option value={168}>7 days</option>
                        <option value={720}>30 days</option>
                      </select>
                    )}
                  </div>
                </li>
              ))}
              {goneItems.map(g => (
                <li
                  className="vault-item vault-item--gone"
                  key={g.id}
                  data-vault-item={g.id}
                  data-vault-gone="true"
                  data-vault-kind={g.kind}
                >
                  <div className="vault-item-head">
                    <span className={`vault-item-kind vault-item-kind--${g.kind}`}>
                      {g.kind === 'letter' ? 'Letter' : g.kind === 'secret' ? 'Secret' : 'Photo'}
                    </span>
                    <span className="vault-item-when">Gone</span>
                  </div>
                  {g.title && <p className="vault-item-title">{g.title}</p>}
                </li>
              ))}
            </ul>
          )}

          {/* Partner re-issue (V5). Only an UNLOCKED member can do this: the
              root key exists on this device right now, so this is the one way
              back in for a partner who forgot their passphrase. */}
          {keys?.ownSlot && keys.wraps.length === 2 && (
            <div className="vault-card vault-partner-reset" id="vault-partner-reset">
              <h3 className="vault-section-title" id="vault-partner-reset-title">
                {partnerPossessive('Your partner’s')} passphrase
              </h3>
              <p className="vault-note">
                If they have forgotten theirs, set a new one here and tell them. You can only do
                this while your own vault is unlocked.
              </p>
              {partnerNote && (
                <p className="vault-note" id="vault-partner-note">
                  {partnerNote}
                </p>
              )}
              {!partnerOpen ? (
                <button
                  type="button"
                  className="vault-link"
                  id="btn-vault-partner-open"
                  onClick={() => {
                    setPartnerOpen(true);
                    setError('');
                  }}
                >
                  Set a new passphrase for them
                </button>
              ) : (
                <form id="vault-partner-form" onSubmit={reissue}>
                  <label className="vault-label" htmlFor="vault-partner-pass">
                    Their new passphrase
                  </label>
                  <input
                    id="vault-partner-pass"
                    className="vault-input"
                    type="password"
                    autoComplete="new-password"
                    value={partnerPass}
                    onChange={e => setPartnerPass(e.target.value)}
                  />
                  <label className="vault-label" htmlFor="vault-partner-pass2">
                    Again
                  </label>
                  <input
                    id="vault-partner-pass2"
                    className="vault-input"
                    type="password"
                    autoComplete="new-password"
                    value={partnerPass2}
                    onChange={e => setPartnerPass2(e.target.value)}
                  />
                  <button type="submit" className="vault-primary" id="btn-vault-partner-reset" disabled={busy}>
                    {busy ? 'Saving…' : 'Set their passphrase'}
                  </button>
                </form>
              )}
            </div>
          )}
        </div>
      )}

      {error && (
        <p className="vault-error" id="vault-error" role="alert">
          {error}
        </p>
      )}
    </section>
  );
}
